"use client";

import { useEffect, useState } from "react";

export interface TocItem {
  id: string;
  label: string;
  /** Visual indent — used for sub-items like the per-extension picker. */
  indent?: boolean;
  /** Optional emphasis token (e.g. for the Limited Use compliance anchor). */
  badge?: string;
}

export interface TocSidebarProps {
  items: TocItem[];
}

export default function TocSidebar({ items }: TocSidebarProps) {
  const [activeId, setActiveId] = useState<string | null>(null);

  // Track which section is currently visible via IntersectionObserver.
  // Picks the topmost visible section so the sidebar feels in-sync with
  // the user's reading position.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const els = items
      .map((i) => document.getElementById(i.id))
      .filter((el): el is HTMLElement => el !== null);
    if (els.length === 0) return;

    const visible = new Map<string, IntersectionObserverEntry>();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            visible.set(entry.target.id, entry);
          } else {
            visible.delete(entry.target.id);
          }
        }
        // Pick the entry whose top is closest to the viewport top (but
        // still above the bottom 60% threshold defined by rootMargin).
        let topId: string | null = null;
        let topY = Infinity;
        for (const e of visible.values()) {
          const y = e.boundingClientRect.top;
          if (y < topY) {
            topY = y;
            topId = e.target.id;
          }
        }
        if (topId) setActiveId(topId);
      },
      {
        // Trigger when a section enters the upper 30% of the viewport.
        rootMargin: "-15% 0% -55% 0%",
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );

    for (const el of els) observer.observe(el);
    return () => observer.disconnect();
  }, [items]);

  return (
    <nav
      aria-label="Privacy policy sections"
      className="sticky top-24 self-start hidden lg:block max-h-[calc(100vh-8rem)] overflow-y-auto rounded-xl border border-border bg-card/60 backdrop-blur-sm p-5 text-sm"
    >
      <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-3">
        On this page
      </p>
      <ul className="space-y-1">
        {items.map((item) => {
          const isActive = activeId === item.id;
          return (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                aria-current={isActive ? "true" : undefined}
                className={`group flex items-center gap-2 rounded-md px-2 py-1.5 transition-colors ${
                  item.indent ? "ml-3 text-xs" : "text-sm"
                } ${
                  isActive
                    ? "bg-primary/10 text-primary font-medium"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`size-1.5 rounded-full transition-colors ${
                    isActive ? "bg-primary" : "bg-border group-hover:bg-foreground/40"
                  }`}
                />
                <span className="flex-1 leading-snug">{item.label}</span>
                {item.badge && (
                  <span className="rounded-full bg-primary/15 text-primary text-[9px] font-mono font-bold uppercase px-1.5 py-0.5">
                    {item.badge}
                  </span>
                )}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
